import type Quill from "quill";
import type { Op } from "quill/core";
import type { Delta } from "quill";
import urlRegexSafe from "url-regex-safe";

const isRetainOp = (op: Op): op is Op & { retain: number } =>
  typeof op.retain === "number";

// Returns the run of non-whitespace text that ends right before `index`.
// Works in Quill index space: embeds (mentions) count as one position and
// terminate the word, so the result can be used directly with formatText.
const getWordEndingAt = (quill: Quill, index: number): string => {
  const ops = quill.getContents(0, index).ops ?? [];
  let word = "";

  for (let i = ops.length - 1; i >= 0; i -= 1) {
    const { insert } = ops[i];
    if (typeof insert !== "string") break;

    for (let j = insert.length - 1; j >= 0; j -= 1) {
      const char = insert[j];
      if (char === " " || char === "\n") return word;
      word = char + word;
    }
  }

  return word;
};

export const quillAutolinkUrl = (quill: Quill, changeDelta: Delta) => {
  // Determine what was just inserted
  let inserted = "";
  changeDelta.ops?.forEach((op) => {
    if (typeof op.insert === "string") {
      inserted += op.insert;
    }
  });

  if (!inserted) return;

  // Where the insert happened, in Quill index space (mentions count as 1).
  const insertIndex = changeDelta.ops?.find(isRetainOp)?.retain ?? 0;

  const isPaste = inserted.length > 1;
  const lastChar = inserted.slice(-1);
  const isWordBoundary = lastChar === " " || lastChar === "\n";

  if (isPaste) {
    // Scan the pasted region for URLs
    const pasteText = quill.getText(insertIndex, inserted.length);
    const urlRegex = urlRegexSafe({ strict: true });

    const matches = pasteText.matchAll(urlRegex);

    for (const match of matches) {
      const url = match[0];
      const start = insertIndex + (match.index ?? 0);
      const existing = quill.getFormat(start, url.length);
      if (!existing.link) {
        quill.formatText(start, url.length, "link", url, "silent");
      }
    }
  } else if (isWordBoundary) {
    // Single space/newline typed — check the word immediately before it.
    // Deliberately derived from the change delta rather than getSelection():
    // getText() skips embeds so its offsets drift by one per mention, and
    // when typing right after a link Quill still reports the pre-insert
    // caret inside text-change. Both used to trim the last URL character
    // and shift the link range when a mention preceded the URL.
    const word = getWordEndingAt(quill, insertIndex);
    if (!word) return;

    const urlRegex = urlRegexSafe({ strict: true });

    if (urlRegex.test(word)) {
      const start = insertIndex - word.length;
      const existing = quill.getFormat(start, word.length);
      if (!existing.link) {
        quill.formatText(start, word.length, "link", word, "silent");
      }
    }
  }
};
